import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import api, { apiError } from "@/lib/api";
import { money } from "@/lib/format";
import { toast } from "sonner";
import { StepFlow } from "@/components/StepFlow";
import { QRCodeCanvas } from "qrcode.react";
import { SimCard, Check, QrCode, X, Copy, AppleLogo } from "@phosphor-icons/react";

function EsimQrModal({ esim, onClose }) {
  const code = esim.activation_code || "";
  const appleLink = `https://esimsetup.apple.com/esim_qrcode_provisioning?carddata=${encodeURIComponent(code)}`;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose} data-testid="esim-qr-modal">
      <div className="w-full max-w-sm rounded-3xl border border-border bg-card p-6 text-center" onClick={(e) => e.stopPropagation()}>
        <div className="mb-2 flex items-center justify-between">
          <h3 className="font-display text-lg font-semibold">Install eSIM · {esim.data_gb} GB</h3>
          <button data-testid="esim-qr-close" onClick={onClose} className="rounded-lg p-1.5 text-muted-foreground hover:bg-secondary"><X size={18} /></button>
        </div>
        <p className="mb-4 text-xs text-muted-foreground">Scan this QR in Settings → Cellular → Add eSIM.</p>
        <div className="mx-auto flex w-fit rounded-2xl bg-white p-4">
          <QRCodeCanvas data-testid="esim-qr-canvas" value={code} size={200} includeMargin={false} level="M" />
        </div>
        <a data-testid="esim-apple-link" href={appleLink} target="_blank" rel="noreferrer"
          className="mt-4 flex items-center justify-center gap-2 rounded-full bg-ink py-3 text-sm font-semibold text-white" style={{ background: "#08111F" }}>
          <AppleLogo size={18} weight="fill" /> Install on iPhone (one tap)
        </a>
        <button data-testid="esim-copy-code" onClick={() => { navigator.clipboard.writeText(code); toast.success("Activation code copied"); }}
          className="mt-2 flex w-full items-center justify-center gap-2 rounded-full border border-border py-2.5 text-sm font-medium">
          <Copy size={16} /> Copy activation code
        </button>
        <p className="mt-3 break-all font-mono-metric text-[10px] text-muted-foreground">{code}</p>
      </div>
    </div>
  );
}

export default function Esim() {
  const qc = useQueryClient();
  const [params] = useSearchParams();
  const wantCode = (params.get("c") || "").toUpperCase();
  const [autoOpened, setAutoOpened] = useState(false);
  const [step, setStep] = useState(0);
  const [country, setCountry] = useState(null);
  const [plan, setPlan] = useState(null);
  const [busy, setBusy] = useState(false);
  const [qrEsim, setQrEsim] = useState(null);

  const { data } = useQuery({
    queryKey: ["esim-catalog", country?.code],
    queryFn: async () => (await api.get(`/esim/catalog?country=${country?.code || "US"}`)).data,
    enabled: step >= 0,
  });
  const { data: mine } = useQuery({ queryKey: ["esim-mine"], queryFn: async () => (await api.get("/esim/mine")).data });
  const countries = data?.countries || [];

  // Deep-link (?c=US) from a tapped SMS/WhatsApp link — jump straight to the plan list.
  useEffect(() => {
    if (autoOpened || !wantCode) return;
    const c = countries.find((x) => x.code === wantCode);
    if (c) { setCountry(c); setStep(1); setAutoOpened(true); }
  }, [wantCode, countries, autoOpened]); // eslint-disable-line

  const buy = async () => {
    setBusy(true);
    try {
      const res = await api.post("/esim/purchase", {
        plan_id: plan.id, country: country.code, data_gb: plan.data_gb,
        validity_days: plan.validity_days, retail_price_cents: plan.retail_price_cents,
      });
      toast.success(res.data?.note || "eSIM activated");
      qc.invalidateQueries({ queryKey: ["wallet"] });
      qc.invalidateQueries({ queryKey: ["esim-mine"] });
      setStep(0); setPlan(null); setCountry(null);
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display text-3xl font-semibold tracking-tight sm:text-4xl">Travel eSIM</h1>
        <p className="mt-2 text-muted-foreground">Destination → Plan → Activate. Instant travel data plans.</p>
      </div>
      <StepFlow steps={["Destination", "Plan", "Activate"]} current={step} />

      {step === 0 && (
        <div className="animate-fade-up grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {countries.map((c) => (
            <button key={c.code} data-testid={`esim-country-${c.code}`} onClick={() => { setCountry(c); setStep(1); }}
              className="flex items-center gap-3 rounded-2xl border border-border bg-card p-4 text-left transition-all hover:-translate-y-0.5 hover:border-primary">
              <span className="text-2xl">{c.flag}</span>
              <p className="text-sm font-semibold">{c.name}</p>
            </button>
          ))}
        </div>
      )}

      {step === 1 && country && (
        <div className="animate-fade-up space-y-4">
          <button onClick={() => setStep(0)} className="rounded-full border border-border px-3 py-1.5 text-sm">← {country.flag} {country.name}</button>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {(data?.plans || []).map((p) => (
              <button key={p.id} data-testid={`esim-plan-${p.id}`} onClick={() => { setPlan(p); setStep(2); }}
                className="rounded-2xl border border-border bg-card p-5 text-left transition-all hover:-translate-y-1 hover:border-primary">
                <SimCard size={24} weight="duotone" className="text-primary" />
                <p className="mt-3 font-display text-2xl font-semibold">{p.data_gb} GB</p>
                <p className="text-xs text-muted-foreground">{p.validity_days} days validity</p>
                <p className="mt-3 font-mono-metric font-semibold">{money(p.retail_price_cents)}</p>
              </button>
            ))}
          </div>
        </div>
      )}

      {step === 2 && plan && (
        <div className="animate-fade-up mx-auto max-w-md rounded-3xl border border-border bg-card p-6">
          <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-accent text-primary"><SimCard size={28} weight="duotone" /></div>
          <h2 className="font-display text-xl font-semibold">Confirm eSIM</h2>
          <dl className="mt-4 space-y-3 text-sm">
            {[["Destination", `${country.flag} ${country.name}`], ["Data", `${plan.data_gb} GB`], ["Validity", `${plan.validity_days} days`]].map(([k, v]) => (
              <div key={k} className="flex justify-between"><dt className="text-muted-foreground">{k}</dt><dd className="font-medium">{v}</dd></div>
            ))}
            <div className="flex justify-between border-t border-border pt-3"><dt className="font-semibold">Total</dt><dd className="font-mono-metric font-semibold">{money(plan.retail_price_cents)}</dd></div>
          </dl>
          <div className="mt-6 flex gap-3">
            <button onClick={() => setStep(1)} className="rounded-full border border-border px-5 py-3 text-sm font-medium">Back</button>
            <button data-testid="esim-activate" onClick={buy} disabled={busy}
              className="flex flex-1 items-center justify-center gap-2 rounded-full bg-primary py-3 font-semibold text-primary-foreground transition-transform hover:scale-[1.02] disabled:opacity-60">
              <Check size={18} weight="bold" /> {busy ? "Activating…" : "Activate eSIM"}
            </button>
          </div>
        </div>
      )}

      {mine?.esims?.length > 0 && (
        <div>
          <h2 className="mb-3 mt-4 text-xs font-semibold uppercase tracking-[0.16em] text-muted-foreground">My eSIMs</h2>
          <div className="grid gap-3 md:grid-cols-3">
            {mine.esims.map((e) => (
              <div key={e.id} className="rounded-2xl border border-border bg-card p-4">
                <div className="flex items-center justify-between">
                  <p className="font-display text-lg font-semibold">{e.data_gb} GB</p>
                  <button data-testid={`esim-showqr-${e.id}`} onClick={() => setQrEsim(e)} className="rounded-lg p-1 text-primary hover:bg-secondary">
                    <QrCode size={22} weight="duotone" />
                  </button>
                </div>
                <p className="text-xs text-muted-foreground">{e.country} · {e.validity_days}d · <span className="text-emerald-600">{e.status}</span></p>
                <button data-testid={`esim-install-${e.id}`} onClick={() => setQrEsim(e)}
                  className="mt-3 w-full rounded-full bg-primary py-2 text-xs font-semibold text-primary-foreground">Install (QR)</button>
              </div>
            ))}
          </div>
        </div>
      )}
      {qrEsim && <EsimQrModal esim={qrEsim} onClose={() => setQrEsim(null)} />}
    </div>
  );
}
